/**
 * ReviewPanel — session tree + proposal timeline, always visible.
 *
 * Session tree: hierarchical view of all sessions with inline action buttons.
 * Selecting a session auto-replays all its proposals to the shadow graph.
 * Clicking individual proposals steps through intermediate states.
 * Metadata for the selected session is shown above the tree.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { curationApi, proposalApi } from '../services/api';
import type {
  AnalyzeSessionRequest,
  FoundryResponseDetails,
  ProposalSession,
  ProposalStep,
  ProposalSubmitEntry,
  SessionTree as SessionTreeModel,
} from '../types';
import SessionTree from './SessionTree';
import FoundryTraceOverlay from './FoundryTraceOverlay';
import './SessionTree.css';
import './ReviewPanel.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ACTION_ICONS: Record<string, string> = { add: 'add', update: 'refresh', delete: 'delete' };

const REJECTION_SNIPPETS = [
  {
    label: 'Bad Name',
    value: 'bad_name',
    text: '`bad_name`\nThe proposal has an inappropriate or unclear name; rename it according to below.',
  },
  {
    label: 'Too Broad',
    value: 'too_broad',
    text: '`too_broad`\nThe proposal is valid in theme but too broad in scope; decompose or narrow it into smaller atomic concepts according to below.',
  },
  {
    label: 'Workflow',
    value: 'workflow',
    text: '`workflow`\nThis proposal describes a workflow or orchestration rather than a single action; decompose it into atomic Functions or concepts according to below.',
  },
  {
    label: 'Non-Atomic',
    value: 'non_atomic',
    text: '`non_atomic`\nThis proposal combines multiple actions, actors, or objects into one node; split or narrow it according to below.',
  },
  {
    label: 'Reparent',
    value: 'reparent',
    text: '`reparent`\nThe concept is valid but belongs elsewhere in the graph; keep the concept and change placement according to below.',
  },
  {
    label: 'Relink',
    value: 'relink',
    text: '`relink`\nThe concept is valid but should be connected through different graph relationships according to below.',
  },
  {
    label: 'Wrong Type',
    value: 'wrong_type',
    text: '`wrong_type`\nThe concept is valid but classified under the wrong node type; retype it according to below.',
  },
  {
    label: 'Duplicate',
    value: 'duplicate',
    text: '`duplicate`\nEquivalent coverage already exists; prefer alias or update over add according to below.',
  },
  {
    label: 'Drop',
    value: 'drop',
    text: '`drop`\nNo valid recoverable concept should be proposed from this candidate.',
  },
] as const;

function formatFieldLabel(fieldName?: string): string {
  const field = (fieldName || '').trim();
  if (field === 'semantic_summary') {
    return 'semantic summary';
  }
  if (field === 'summary_state') {
    return 'summary state';
  }
  return field;
}

function actionBadgeClass(action: string): string {
  if (action === 'add') return 'badge-staged-add';
  if (action === 'delete') return 'badge-staged-delete';
  return 'badge-staged-update';
}

/**
 * A grouped preview entry. Non-update entries that share a
 * `proposal_step_id` collapse into a single review step. Field-level node
 * updates remain standalone so they can be reviewed and rejected independently.
 * The group's `endIndex` tells the preview system to apply all entries
 * through that step.
 */
interface ProposalGroup {
  groupId: string;
  groupType?: ProposalSession['stepGroups'][number]['groupType'];
  label?: string;
  /** The lead preview entry (typically the node "add"). */
  lead: ProposalSubmitEntry;
  /** All preview entries in this group (lead + relationships). */
  members: ProposalSubmitEntry[];
  /** Index of the first member in the flattened grouped preview array. */
  startIndex: number;
  /** Index of the last member in the flattened grouped preview array. */
  endIndex: number;
}

interface QueueGroup {
  lead: ProposalSubmitEntry;
  members: ProposalSubmitEntry[];
  startIndex: number;
  endIndex: number;
}

type SessionProposalGroupCandidate = Omit<ProposalGroup, 'startIndex' | 'endIndex'> & {
  lead?: ProposalSubmitEntry;
};

function hasProposalGroupMembers(
  group: SessionProposalGroupCandidate
): group is Omit<ProposalGroup, 'startIndex' | 'endIndex'> {
  return group.members.length > 0 && Boolean(group.lead);
}

function withPreviewBounds<T extends { lead: unknown; members: unknown[] }>(
  groups: T[]
): Array<T & { startIndex: number; endIndex: number }> {
  let cursor = 0;
  return groups.map((group) => {
    const startIndex = cursor;
    const endIndex = cursor + group.members.length - 1;
    cursor = endIndex + 1;
    return { ...group, startIndex, endIndex };
  });
}

function stepAction(step: ProposalStep): ProposalSubmitEntry['action'] {
  switch (step.operation) {
    case 'add_node':
    case 'add_relationship':
    case 'add_alias':
      return 'add';
    case 'delete_node':
    case 'delete_relationship':
    case 'remove_alias':
      return 'delete';
    default:
      return 'update';
  }
}

function stepEntityType(step: ProposalStep): ProposalSubmitEntry['entity_type'] {
  switch (step.operation) {
    case 'add_relationship':
    case 'delete_relationship':
      return 'relationship';
    default:
      return 'node';
  }
}

function stringifyStepValue(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function getStepPreviousValue(step: ProposalStep): string | undefined {
  const candidate = step.field_previous_value;
  if (candidate != null) {
    return stringifyStepValue(candidate);
  }

  const legacyStep = step as ProposalStep & {
    proposed_property_previous_value?: unknown;
    previous_value?: unknown;
    original_value?: unknown;
  };

  return stringifyStepValue(
    legacyStep.proposed_property_previous_value ??
      legacyStep.previous_value ??
      legacyStep.original_value
  );
}

function stepToPreviewEntry(step: ProposalStep): ProposalSubmitEntry {
  const action = stepAction(step);
  const entity_type = stepEntityType(step);
  const resolvedNodeId =
    step.logical_target_id?.trim() || step.node_properties?.id?.toString().trim() || undefined;
  const entry: ProposalSubmitEntry = {
    action,
    entity_type,
    operation: step.operation,
    proposal_step_id: step.step_id,
  };

  if (entity_type === 'relationship') {
    entry.rel_type = step.relationship_type || step.field_name || undefined;
    entry.from_node_id = step.from_node_id || undefined;
    entry.to_node_id = step.to_node_id || undefined;

    if ((!entry.rel_type || !entry.from_node_id || !entry.to_node_id) && step.logical_target_id) {
      const [fromNodeId = '', toNodeId = '', relType = ''] = step.logical_target_id.split(':');
      entry.rel_type = entry.rel_type || relType || undefined;
      entry.from_node_id = entry.from_node_id || fromNodeId || undefined;
      entry.to_node_id = entry.to_node_id || toNodeId || undefined;
    }
    return entry;
  }

  entry.node_id = resolvedNodeId;
  entry.field_name = step.field_name || undefined;
  entry.field_value = stringifyStepValue(step.field_value);
  entry.field_previous_value = getStepPreviousValue(step);

  switch (step.operation) {
    case 'add_node':
      entry.field_name = 'id';
      entry.field_value = step.logical_target_id || entry.field_value;
      entry.node_name = step.node_name || entry.node_id;
      entry.node_type = step.node_type || undefined;
      entry.node_description = step.node_description || undefined;
      entry.node_properties = step.node_properties || undefined;
      if (Array.isArray(step.node_properties?.aliases)) {
        entry.node_name = step.node_name || entry.node_id;
      }
      break;
    case 'set_name':
      entry.node_name = entry.field_value;
      break;
    case 'set_description':
      entry.node_description = entry.field_value;
      break;
    case 'set_type':
      entry.node_type = entry.field_value;
      break;
    case 'set_external_id':
    case 'set_location':
    case 'add_alias':
    case 'remove_alias':
    case 'delete_node':
    default:
      break;
  }

  return entry;
}

function stepVerb(proposal: ProposalSubmitEntry): string {
  switch (proposal.operation) {
    case 'skip':
      return 'Skip';
    case 'add_alias':
      return 'Add alias';
    case 'remove_alias':
      return 'Remove alias';
    case 'delete_node':
      return 'Delete';
    case 'delete_relationship':
      return 'Delete';
    case 'add_node':
    case 'add_relationship':
      return 'Add';
    default:
      return proposal.action === 'add' ? 'Add' : proposal.action === 'delete' ? 'Delete' : 'Update';
  }
}

function proposalGroupTitle(group: ProposalGroup): string {
  const explicitLabel = (group.label || '').trim();
  if (explicitLabel) {
    return explicitLabel;
  }

  const lead = group.lead;
  const verb = stepVerb(lead).toLowerCase();
  const entityLabel = lead.entity_type === 'relationship' ? 'relationship' : 'node';
  const targetId = lead.node_id ?? lead.to_node_id ?? lead.from_node_id;
  if (targetId) {
    return `${verb} ${entityLabel} ${targetId}`;
  }
  return `${verb} ${entityLabel}`;
}

function proposalGroupDisplayTitle(group: ProposalGroup): string {
  return (group.label || '').trim() || proposalGroupTitle(group);
}

function sessionStepsToPreviewEntries(steps: ProposalStep[]): ProposalSubmitEntry[] {
  return steps.filter((step): step is ProposalStep => Boolean(step)).map(stepToPreviewEntry);
}

function flattenSessionSteps(session: ProposalSession | null): ProposalStep[] {
  if (!session) {
    return [];
  }

  return (session.stepGroups ?? []).flatMap((group) => group.steps ?? []);
}

function sessionHasRejectedSteps(session: ProposalSession | null): boolean {
  return flattenSessionSteps(session).some((step) => step.review_status === 'rejected');
}

function sessionHasGroupedSteps(session: ProposalSession | null): boolean {
  return flattenSessionSteps(session).some((step) => (step.step_id || '').trim() !== '');
}

function flattenSessionTree(tree: SessionTreeModel | null): ProposalSession[] {
  if (!tree) {
    return [];
  }

  const current = tree.session ? [tree.session] : [];
  return current.concat(...(tree.branches ?? []).map((branch) => flattenSessionTree(branch)));
}

function sessionPreviewEntries(session: ProposalSession | null): ProposalSubmitEntry[] {
  if (!session) {
    return [];
  }

  return sessionStepsToPreviewEntries(flattenSessionSteps(session));
}

function sessionProposalGroups(session: ProposalSession | null): ProposalGroup[] {
  if (!session) {
    return [];
  }

  const stepGroups = session.stepGroups ?? [];
  if (stepGroups.length > 0) {
    return withPreviewBounds(
      stepGroups
        .map((group) => {
          const members = sessionStepsToPreviewEntries(group.steps ?? []);
          return {
            groupId: group.groupId,
            groupType: group.groupType,
            label: group.label,
            lead: members[0],
            members,
          };
        })
        .filter(hasProposalGroupMembers)
    );
  }

  return [];
}

function buildSessionPreviewModel(session: ProposalSession | null): {
  groups: ProposalGroup[];
  proposals: ProposalSubmitEntry[];
} {
  const groups = sessionProposalGroups(session);
  return {
    groups,
    proposals: groups.flatMap((group) => group.members),
  };
}

function replaceSessionInTree(
  tree: SessionTreeModel | null,
  updated: ProposalSession
): SessionTreeModel | null {
  if (!tree) {
    return tree;
  }

  return {
    ...tree,
    session: tree.session?.sessionId === updated.sessionId ? updated : tree.session,
    branches: (tree.branches ?? []).map(
      (branch) => replaceSessionInTree(branch, updated) as SessionTreeModel
    ),
  };
}

function appendSessionToRoot(
  tree: SessionTreeModel | null,
  session: ProposalSession
): SessionTreeModel {
  if (!tree) {
    return {
      session: {
        sessionId: 'live',
        status: 'approved',
        title: 'Live Graph',
        agentAuthor: false,
        submittedBy: '',
        submittedAt: '',
        stepGroups: [],
      },
      selectedSessionId: session.sessionId,
      branches: [{ session, branches: [] }],
    };
  }

  return {
    ...tree,
    branches: [...(tree.branches ?? []), { session, branches: [] }],
  };
}

function removeSessionFromTree(
  tree: SessionTreeModel | null,
  sessionId: string
): SessionTreeModel | null {
  if (!tree) {
    return tree;
  }

  return {
    ...tree,
    branches: (tree.branches ?? [])
      .filter((branch) => branch.session?.sessionId !== sessionId)
      .map((branch) => removeSessionFromTree(branch, sessionId) as SessionTreeModel),
  };
}

function groupQueueEntries(entries: readonly ProposalSubmitEntry[]): QueueGroup[] {
  const groups: Array<Omit<QueueGroup, 'startIndex' | 'endIndex'>> = [];
  const groupMap = new Map<string, Array<Omit<QueueGroup, 'startIndex' | 'endIndex'>>[number]>();

  for (const entry of entries) {
    const key = entry.proposal_step_id;
    const isStandaloneUpdate = entry.action === 'update';
    if (!key || isStandaloneUpdate) {
      groups.push({ lead: entry, members: [entry] });
      continue;
    }

    const existing = groupMap.get(key);
    if (existing) {
      existing.members.push(entry);
      if (existing.lead.entity_type === 'relationship' && entry.entity_type === 'node') {
        existing.lead = entry;
      }
      continue;
    }

    const group = { lead: entry, members: [entry] };
    groupMap.set(key, group);
    groups.push(group);
  }

  return withPreviewBounds(groups);
}

// ---------------------------------------------------------------------------
// ProposalItem — single row in the timeline (may represent a group)
// ---------------------------------------------------------------------------

interface ProposalItemProps {
  proposal: ProposalSubmitEntry;
  step?: ProposalStep;
  index: number;
  highlighted: boolean;
  onClick?: () => void;
  /** Number of relationship proposals grouped with this node (0 = standalone). */
  relCount?: number;
  /** Step identifier resolved from the full grouped step, not just the lead proposal. */
  stepId?: string;
  /** Whether the current user can reject steps. */
  canReject?: boolean;
  /** Callback to reject this step. */
  onRejectStep?: (stepId: string, reason: string) => void;
  /** Callback to restore this step to pending. */
  onUnrejectStep?: (stepId: string) => void;
  /** Whether the step is already rejected. */
  isRejected?: boolean;
  /** The rejection reason if already rejected. */
  rejectionReason?: string;
  /** Whether a rejection action is in progress. */
  busy?: boolean;
  /** Disable preview click behavior while preserving inline actions like reject. */
  disablePreviewClick?: boolean;
}

const ProposalItem: React.FC<ProposalItemProps> = ({
  proposal,
  step,
  index,
  highlighted,
  onClick,
  relCount,
  stepId: resolvedStepId,
  canReject,
  onRejectStep,
  onUnrejectStep,
  isRejected,
  rejectionReason,
  busy,
  disablePreviewClick,
}) => {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showSnippetMenu, setShowSnippetMenu] = useState(false);
  const action = proposal.action ?? '';
  const icon = ACTION_ICONS[action] ?? '?';
  const isRel = proposal.entity_type === 'relationship';
  const label = proposal.node_name || proposal.node_id || '?';
  const stepId = resolvedStepId?.trim() || proposal.proposal_step_id?.trim() || '';
  const badgeEl = (
    <span className={`rp-badge-small ${actionBadgeClass(action)}`}>
      <i className="modus-icons" aria-hidden="true">
        {icon}
      </i>
    </span>
  );
  const indexEl = <span className="rp-proposal-index">{index + 1}.</span>;

  const justification =
    typeof proposal.justification === 'string' ? proposal.justification.trim() : '';
  const stepJustification =
    typeof step?.justification === 'string' ? step.justification.trim() : '';
  const effectiveJustification = stepJustification || justification;
  const justificationDetails = effectiveJustification ? (
    <details className="rp-justification-details" onClick={(e) => e.stopPropagation()}>
      <summary>Justification</summary>
      <div className="rp-justification">
        <p>{effectiveJustification}</p>
      </div>
    </details>
  ) : null;
  const nodeType = step?.node_type || proposal.node_type;
  const logicalTargetID = step?.logical_target_id?.trim() || '';
  const nodeProperties =
    step?.node_properties && typeof step.node_properties === 'object'
      ? Object.entries(step.node_properties).filter(
          ([, value]) => value !== null && value !== undefined && String(value).trim() !== ''
        )
      : [];
  const showNodeStepDetails =
    Boolean(step) && !isRel && proposal.entity_type === 'node' && action !== 'update';

  const handleRejectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRejectForm(true);
  };

  const handleRejectSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (rejectReason.trim() && stepId && onRejectStep) {
      onRejectStep(stepId, rejectReason.trim());
      setShowRejectForm(false);
      setRejectReason('');
      setShowSnippetMenu(false);
    }
  };

  const handleRejectCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRejectForm(false);
    setRejectReason('');
    setShowSnippetMenu(false);
  };

  const handleSnippetInsert = (snippetValue: string) => {
    const snippet = REJECTION_SNIPPETS.find((item) => item.value === snippetValue);
    if (!snippet) {
      return;
    }
    setRejectReason((current) => {
      const trimmed = current.trim();
      if (!trimmed) {
        return snippet.text;
      }
      if (trimmed.includes(snippet.text)) {
        return current;
      }
      return `${current.replace(/\s*$/, '')}\n\n${snippet.text}`;
    });
    setShowSnippetMenu(false);
  };

  const handleUnrejectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (stepId && onUnrejectStep) {
      onUnrejectStep(stepId);
    }
  };

  const rejectIconEl =
    canReject && stepId && !isRejected ? (
      <button
        className="rp-step-reject-btn"
        title="Reject this step"
        disabled={busy}
        onClick={handleRejectClick}
      >
        <i className="modus-icons" aria-hidden="true">
          cancel_circle
        </i>
      </button>
    ) : canReject && stepId && isRejected ? (
      <button
        className="rp-step-reject-btn"
        title="Restore this step to pending"
        disabled={busy}
        onClick={handleUnrejectClick}
      >
        <i className="modus-icons" aria-hidden="true">
          undo
        </i>
      </button>
    ) : null;

  const rejectedBanner = isRejected ? (
    <div className="rp-step-rejected-reason">
      <span className="rp-step-rejected-label">Rejected:</span>{' '}
      {rejectionReason || 'No reason provided'}
    </div>
  ) : null;

  const rejectFormEl = showRejectForm ? (
    <div className="rp-step-reject-form" onClick={(e) => e.stopPropagation()}>
      <textarea
        className="rp-step-reject-input"
        rows={3}
        placeholder="Rejection reason…"
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        autoFocus
      />
      <div className="rp-step-reject-actions">
        <div className="rp-step-reject-actions-left">
          <div className="rp-step-snippet-menu">
            <button
              type="button"
              className="btn-secondary btn-sm rp-step-snippet-trigger"
              onClick={(e) => {
                e.stopPropagation();
                setShowSnippetMenu((current) => !current);
              }}
            >
              Snippets
            </button>
            {showSnippetMenu && (
              <div className="rp-step-snippet-popover">
                {REJECTION_SNIPPETS.map((snippet) => (
                  <button
                    key={snippet.value}
                    type="button"
                    className="rp-step-snippet-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSnippetInsert(snippet.value);
                    }}
                  >
                    <span className="rp-step-snippet-option-label">{snippet.label}</span>
                    <span className="rp-step-snippet-option-value">{snippet.value}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button className="btn-secondary btn-sm" onClick={handleRejectCancel}>
          Cancel
        </button>
        <button
          className="btn-danger btn-sm"
          disabled={!rejectReason.trim()}
          onClick={handleRejectSave}
        >
          Save
        </button>
      </div>
    </div>
  ) : null;

  const itemClass = `rp-proposal-item${isRejected ? ' rp-proposal-item--rejected' : ''}${highlighted && !isRejected ? ' rp-proposal-item--active' : ''}`;
  const previewClickHandler = !disablePreviewClick && !isRejected ? onClick : undefined;
  const previewTitle = isRejected
    ? 'This step has been rejected'
    : disablePreviewClick
      ? undefined
      : 'Click to preview graph up to this change';

  const nodeStepDetails = showNodeStepDetails ? (
    <div className="rp-proposal-details">
      {nodeType && (
        <div className="rp-proposal-detail-row">
          <strong className="rp-proposal-detail-label">Type:</strong>{' '}
          <span className="rp-proposal-detail-value">{nodeType}</span>
        </div>
      )}
      {logicalTargetID && (
        <div className="rp-proposal-detail-row">
          <strong className="rp-proposal-detail-label">Target:</strong>{' '}
          <span className="rp-proposal-detail-value">{logicalTargetID}</span>
        </div>
      )}
      {nodeProperties.map(([key, value]) => (
        <div key={key} className="rp-proposal-detail-row">
          <strong className="rp-proposal-detail-label">{formatFieldLabel(key)}:</strong>
          <span className="rp-proposal-detail-value">&nbsp;{String(value)}</span>
        </div>
      ))}
      {justificationDetails}
    </div>
  ) : null;

  if (!isRel && action === 'update' && proposal.operation !== 'skip') {
    const field = formatFieldLabel(proposal.field_name);
    const newVal = proposal.field_value ?? '';
    const prevVal = proposal.field_previous_value ?? '';
    return (
      <div
        className={`${itemClass} rp-proposal-item--update`}
        onClick={previewClickHandler}
        title={previewTitle}
      >
        {badgeEl}
        {indexEl}
        <div className="rp-proposal-main">
          <div className="rp-proposal-title">
            Update <strong>&ldquo;{label}&rdquo;</strong> —{' '}
            <span className="rp-diff-field">{field}</span>
          </div>
          <div className="rp-proposal-diff">
            <div className="rp-diff-row rp-diff-before">
              <span className="rp-diff-label">Before</span>
              <span className="rp-diff-value">
                {prevVal || <em className="rp-diff-empty">(empty)</em>}
              </span>
            </div>
            <div className="rp-diff-row rp-diff-after">
              <span className="rp-diff-label">After</span>
              <span className="rp-diff-value">
                {newVal || <em className="rp-diff-empty">(empty)</em>}
              </span>
            </div>
            {justificationDetails}
          </div>
          {rejectedBanner}
          {rejectFormEl}
        </div>
        {rejectIconEl}
      </div>
    );
  }

  let summary: string;
  if (isRel) {
    summary = `${stepVerb(proposal)} relationship: ${proposal.from_node_id ?? '?'} → ${proposal.to_node_id ?? '?'}${proposal.rel_type ? ` (${proposal.rel_type})` : ''}`;
  } else if (proposal.operation === 'add_alias' || proposal.operation === 'remove_alias') {
    const aliasAction = proposal.operation === 'add_alias' ? 'Add' : 'Remove';
    const aliasValue = proposal.field_value ?? '';
    summary = `${aliasAction} alias: ${aliasValue || '(empty)'}${label ? ` -> ${label}` : ''}`;
  } else {
    const relSuffix =
      relCount && relCount > 0 ? ` + ${relCount} rel${relCount > 1 ? 's' : ''}` : '';
    summary = `${stepVerb(proposal)} node: ${label}${proposal.node_type ? ` (${proposal.node_type})` : ''}${relSuffix}`;
  }

  return (
    <div className={itemClass} onClick={previewClickHandler} title={previewTitle}>
      {badgeEl}
      {indexEl}
      <div className="rp-proposal-main">
        <span className="rp-proposal-title">{summary}</span>
        {nodeStepDetails}
        {!nodeStepDetails && justificationDetails}
        {rejectedBanner}
        {rejectFormEl}
      </div>
      {rejectIconEl}
    </div>
  );
};

// ---------------------------------------------------------------------------
// StagedEntryRow — locally queued edit (not yet submitted)
// ---------------------------------------------------------------------------

interface StagedEntryRowProps {
  entry: ProposalSubmitEntry;
  index: number;
  highlighted: boolean;
  onClick: () => void;
  relCount?: number;
}

const StagedEntryRow: React.FC<StagedEntryRowProps> = ({
  entry,
  index,
  highlighted,
  onClick,
  relCount,
}) => {
  const icon = ACTION_ICONS[entry.action] ?? '?';
  const badgeClass = actionBadgeClass(entry.action);
  const badgeEl = (
    <span className={`rp-badge-small ${badgeClass}`}>
      <i className="modus-icons" aria-hidden="true">
        {icon}
      </i>
    </span>
  );
  const indexEl = <span className="rp-proposal-index">{index + 1}.</span>;

  if (entry.entity_type === 'node' && entry.action === 'update' && entry.field_name) {
    const updateEntry = entry as ProposalSubmitEntry & { field_previous_value?: string };
    const label = entry.node_name || entry.node_id || '?';
    const prevVal = updateEntry.field_previous_value ?? '';
    return (
      <div
        className={`rp-proposal-item rp-proposal-item--update${highlighted ? ' rp-proposal-item--active' : ''}`}
        onClick={onClick}
        title="Click to preview graph up to this change"
      >
        {badgeEl}
        {indexEl}
        <div className="rp-proposal-main">
          <div className="rp-proposal-title">
            Update <strong>&ldquo;{label}&rdquo;</strong> —{' '}
            <span className="rp-diff-field">{entry.field_name}</span>
          </div>
          <div className="rp-proposal-diff">
            <div className="rp-diff-row rp-diff-before">
              <span className="rp-diff-label">Before</span>
              <span className="rp-diff-value">
                {prevVal || <em className="rp-diff-empty">(empty)</em>}
              </span>
            </div>
            <div className="rp-diff-row rp-diff-after">
              <span className="rp-diff-label">After</span>
              <span className="rp-diff-value">
                {entry.field_value || <em className="rp-diff-empty">(empty)</em>}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  let summary: string;
  if (entry.entity_type === 'node') {
    const label = entry.node_name || entry.node_id || '?';
    const relSuffix =
      relCount && relCount > 0 ? ` + ${relCount} rel${relCount > 1 ? 's' : ''}` : '';
    summary = `${entry.action === 'add' ? 'Add' : 'Delete'} node: ${label}${entry.node_type ? ` (${entry.node_type})` : ''}${relSuffix}`;
  } else {
    summary = `${entry.action === 'add' ? 'Add' : 'Delete'} relationship: ${entry.from_node_id} → ${entry.to_node_id}${entry.rel_type ? ` (${entry.rel_type})` : ''}`;
  }

  return (
    <div
      className={`rp-proposal-item${highlighted ? ' rp-proposal-item--active' : ''}`}
      onClick={onClick}
      title="Click to preview graph up to this change"
    >
      {badgeEl}
      {indexEl}
      <div className="rp-proposal-main">
        <span className="rp-proposal-title">{summary}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ReviewPanel (main export)
// ---------------------------------------------------------------------------

export interface ReviewPanelProps {
  onProposalActioned?: () => void;
  /** Locally-staged queue entries from the current edit session (not yet submitted). */
  localQueue?: readonly ProposalSubmitEntry[];
  /**
   * Called when the user changes the rendered proposal set for a backend session.
   * null or an empty array clears the preview.
   */
  onPreviewSession?: (proposals: ProposalSubmitEntry[], selectedIndices: number[] | null) => void;
  /**
   * Called when the user clicks a staged edit in the local queue.
   * null clears the preview.
   */
  onPreviewLocalIndex?: (queue: readonly ProposalSubmitEntry[], index: number | null) => void;
  /** Called when the user selects a different session in the tree: null = Live Graph. */
  onSelectedSessionChange?: (sessionId: string | null) => void;
  /** Resume an existing open session. */
  onResume?: (sessionId: string) => void;
  /** Cancel/discard an open session. */
  onCancelSession?: (sessionId: string) => Promise<void>;
  /** Start a new child session branching from an existing session. */
  onModify?: (parentSessionId: string) => void;
  /** Approve a submitted session. */
  onApprove?: (sessionId: string) => void;
  /** Whether the current user can approve sessions. */
  canApprove?: boolean;
  /** ID of the currently active edit session (empty or undefined when none). */
  activeSessionId?: string;
  /** Submit (save) the currently active session. */
  onSaveSession?: () => void;
  /** Cancel the currently active session. */
  onCancelActiveSession?: () => void;
  /** Trigger session list refresh from external events. */
  sessionRefreshKey?: number;
  /** Open source content for a proposal. */
  onViewSource?: (indexName: string, documentId: string, chunk: string | null) => void;
  /** Externally request a session to be selected (e.g. from ContentPanel badge click). */
  requestedSessionId?: string | null;
}

const LOCAL_SESSION_KEY = '__local__';

const ReviewPanel: React.FC<ReviewPanelProps> = ({
  onProposalActioned,
  localQueue = [],
  onPreviewSession,
  onPreviewLocalIndex,
  onSelectedSessionChange,
  onResume,
  onCancelSession,
  onModify,
  canApprove = false,
  activeSessionId,
  onSaveSession,
  onCancelActiveSession,
  sessionRefreshKey = 0,
  onViewSource,
  requestedSessionId,
}) => {
  const [sessionTree, setSessionTree] = useState<SessionTreeModel | null>(null);
  const [activeChainProposals, setActiveChainProposals] = useState<ProposalSubmitEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionIdRaw] = useState<string>(LOCAL_SESSION_KEY);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [selectedPreviewIndices, setSelectedPreviewIndices] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [expandedSummarySessionIds, setExpandedSummarySessionIds] = useState<
    Record<string, boolean>
  >({});
  const [expandedStepsSessionIds, setExpandedStepsSessionIds] = useState<Record<string, boolean>>(
    {}
  );
  const [expandedGroupIds, setExpandedGroupIds] = useState<Record<string, boolean>>({});
  const [traceSessionId, setTraceSessionId] = useState<string | null>(null);
  const [traceDetails, setTraceDetails] = useState<FoundryResponseDetails | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState<string | null>(null);

  const setSelectedSessionId = useCallback(
    (sid: string) => {
      setSelectedSessionIdRaw(sid);
      onSelectedSessionChange?.(sid === LOCAL_SESSION_KEY ? null : sid);
    },
    [onSelectedSessionChange]
  );

  const removeSessionFromSnapshot = useCallback(
    (sessionId: string) => {
      setSessionTree((prev) => removeSessionFromTree(prev, sessionId));
      setSelectedSessionIdRaw((prev) => {
        const next = prev === sessionId ? LOCAL_SESSION_KEY : prev;
        onSelectedSessionChange?.(next === LOCAL_SESSION_KEY ? null : next);
        return next;
      });
      setActiveChainProposals([]);
      setPreviewIndex(null);
      setSelectedPreviewIndices([]);
      onPreviewSession?.([], []);
      onPreviewLocalIndex?.([], null);
    },
    [onPreviewSession, onPreviewLocalIndex, onSelectedSessionChange]
  );

  const fetchSessions = useCallback(async () => {
    setError(null);
    try {
      const list = await proposalApi.listSessions();
      setSessionTree(list);
    } catch (err) {
      setError((err as Error).message || 'Failed to load sessions');
    }
  }, []);

  const sessions = useMemo(
    () => flattenSessionTree(sessionTree).filter((session) => session.sessionId !== 'live'),
    [sessionTree]
  );

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Keep selected session valid when sessions list changes.
  // If a session was externally requested but isn't in the active list, re-inject it.
  useEffect(() => {
    if (selectedSessionId === LOCAL_SESSION_KEY) return;
    if (sessions.some((s) => s.sessionId === selectedSessionId)) return;
    // Externally requested session not in list (closed session) — re-fetch and inject.
    if (requestedSessionId === selectedSessionId) {
      proposalApi
        .getSession(selectedSessionId)
        .then((fetched) => {
          setSessionTree((prev) => {
            if (flattenSessionTree(prev).some((s) => s.sessionId === fetched.sessionId)) {
              return prev;
            }
            return appendSessionToRoot(prev, fetched);
          });
        })
        .catch(() => {
          setSelectedSessionId(LOCAL_SESSION_KEY);
        });
      return;
    }
    setSelectedSessionId(LOCAL_SESSION_KEY);
  }, [sessions, selectedSessionId, setSelectedSessionId, requestedSessionId]);

  // When the active session changes (new session created or session ended), refresh
  // the session list so the tree picks up the newly created session immediately.
  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId, sessionRefreshKey]);

  // Respond to external session selection requests (e.g. ContentPanel badge click).
  // If the session isn't in the active list (e.g. already approved/rejected), fetch it
  // and inject it so the timeline can render.
  useEffect(() => {
    if (!requestedSessionId || requestedSessionId === selectedSessionId) return;
    if (sessions.some((s) => s.sessionId === requestedSessionId)) {
      setSelectedSessionId(requestedSessionId);
      return;
    }
    // Session not in active list — fetch directly.
    proposalApi
      .getSession(requestedSessionId)
      .then((fetched) => {
        setSessionTree((prev) => {
          if (flattenSessionTree(prev).some((s) => s.sessionId === fetched.sessionId)) {
            return prev;
          }
          return appendSessionToRoot(prev, fetched);
        });
        setSelectedSessionId(fetched.sessionId);
      })
      .catch(() => {
        // Session may have been deleted; ignore.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSessionId]);

  const isLocal = selectedSessionId === LOCAL_SESSION_KEY;

  const activeSession = isLocal
    ? null
    : (sessions.find((s) => s.sessionId === selectedSessionId) ?? null);

  // True when the user has clicked the currently-being-edited session in the tree.
  const isViewingActiveEdit =
    !isLocal && !!activeSessionId && selectedSessionId === activeSessionId;
  const sessionPreviewModel = useMemo(() => buildSessionPreviewModel(activeSession), [activeSession]);
  const groupedProposals = sessionPreviewModel.groups;
  const sessionPreviewProposals = sessionPreviewModel.proposals;
  const sessionSummaries = useMemo(
    () =>
      Object.fromEntries(
        sessions
          .filter((session) => Boolean(session.agentSummary))
          .map((session) => [session.sessionId, session.agentSummary as string])
      ),
    [sessions]
  );
  const groupedLocalEntries = useMemo(() => groupQueueEntries(localQueue), [localQueue]);
  const localPreviewQueue = useMemo(
    () => groupedLocalEntries.flatMap((group) => group.members),
    [groupedLocalEntries]
  );

  useEffect(() => {
    if (!activeSession) {
      setExpandedGroupIds({});
      return;
    }

    setExpandedGroupIds((current) => {
      const next: Record<string, boolean> = {};
      for (const group of groupedProposals) {
        next[group.groupId] = current[group.groupId] ?? false;
      }
      return next;
    });
  }, [activeSession, groupedProposals]);
  const proposalSourceTargets = useMemo(() => {
    const toTarget = (
      candidate: Pick<
        ProposalSubmitEntry,
        'source_index' | 'source_document_id' | 'source_chunk_id'
      > | null
    ) => {
      if (!candidate?.source_index || !candidate.source_document_id) {
        return null;
      }
      return {
        indexName: candidate.source_index,
        documentId: candidate.source_document_id,
        chunk: candidate.source_chunk_id ?? null,
      };
    };

    const targets: Record<string, { indexName: string; documentId: string; chunk: string | null }> =
      {};

    for (const session of sessions) {
      const sessionTarget = toTarget({
        source_index: session.sourceIndex,
        source_document_id: session.sourceDocumentId,
        source_chunk_id: session.sourceChunkId,
      });

      if (sessionTarget) {
        targets[session.sessionId] = sessionTarget;
        continue;
      }

      const previewEntries = sessionPreviewEntries(session);
      for (let index = previewEntries.length - 1; index >= 0; index -= 1) {
        const fallbackTarget = toTarget(previewEntries[index] ?? null);
        if (fallbackTarget) {
          targets[session.sessionId] = fallbackTarget;
          break;
        }
      }
    }

    return targets;
  }, [sessions]);

  // Load the full proposal chain for the selected session and auto-preview.
  useEffect(() => {
    let cancelled = false;

    const loadChain = async () => {
      if (!activeSession) {
        setActiveChainProposals([]);
        setPreviewIndex(null);
        setSelectedPreviewIndices([]);
        onPreviewSession?.([], []);
        onPreviewLocalIndex?.([], null);
        return;
      }

      const sessionMap = new Map(sessions.map((s) => [s.sessionId, s]));
      const lineage: string[] = [];
      let cursor: ProposalSession | undefined = activeSession;
      const visited = new Set<string>();

      while (cursor && !visited.has(cursor.sessionId)) {
        visited.add(cursor.sessionId);
        lineage.unshift(cursor.sessionId);
        const parent = cursor.dependsUponSession;
        if (!parent || parent === 'live') break;
        cursor = sessionMap.get(parent);
      }

      try {
        const fullChain = await Promise.all(lineage.map((sid) => proposalApi.getSession(sid)));
        if (cancelled) return;
        const proposals = fullChain.flatMap((session) =>
          sessionStepsToPreviewEntries(flattenSessionSteps(session))
        );
        const previewProposals = fullChain.flatMap((session) => sessionPreviewEntries(session));
        setActiveChainProposals(proposals);
        // Auto-preview: show final state of the entire chain.
        if (previewProposals.length > 0) {
          const lastIdx = previewProposals.length - 1;
          setPreviewIndex(lastIdx);
          const allIndices = previewProposals.map((_, index) => index);
          setSelectedPreviewIndices(allIndices);
          onPreviewSession?.(previewProposals, allIndices);
        } else {
          setPreviewIndex(null);
          setSelectedPreviewIndices([]);
          onPreviewSession?.([], []);
        }
      } catch {
        if (!cancelled) {
          const proposals = sessionStepsToPreviewEntries(flattenSessionSteps(activeSession));
          const previewProposals = sessionPreviewEntries(activeSession);
          setActiveChainProposals(proposals);
          if (previewProposals.length > 0) {
            const lastIdx = previewProposals.length - 1;
            setPreviewIndex(lastIdx);
            const allIndices = previewProposals.map((_, index) => index);
            setSelectedPreviewIndices(allIndices);
            onPreviewSession?.(previewProposals, allIndices);
          } else {
            setSelectedPreviewIndices([]);
            onPreviewSession?.([], []);
          }
        }
      }
    };

    loadChain();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSession,
    activeSession?.sessionId,
    sessions,
    isViewingActiveEdit ? localQueue.length : 0,
  ]);

  const toggleSessionSummary = useCallback(
    (sessionId: string) => {
      setSelectedSessionId(sessionId);
      setExpandedSummarySessionIds((current) => ({
        ...current,
        [sessionId]: !current[sessionId],
      }));
    },
    [setSelectedSessionId]
  );

  const toggleSessionSteps = useCallback(
    (sessionId: string) => {
      setSelectedSessionId(sessionId);
      setExpandedStepsSessionIds((current) => ({
        ...current,
        [sessionId]: !current[sessionId],
      }));
    },
    [setSelectedSessionId]
  );

  const handleShowSessionSource = useCallback(
    (sessionId: string) => {
      const target = proposalSourceTargets[sessionId];
      if (!target || !onViewSource) {
        return;
      }
      onViewSource(target.indexName, target.documentId, target.chunk);
      setSelectedSessionId(sessionId);
    },
    [onViewSource, proposalSourceTargets, setSelectedSessionId]
  );

  const handleShowTrace = useCallback(async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setTraceSessionId(sessionId);
    setTraceLoading(true);
    setTraceError(null);
    try {
      const details = await proposalApi.getFoundryResponseDetails(sessionId);
      setTraceDetails(details);
    } catch (err) {
      setTraceDetails(null);
      setTraceError((err as Error).message || 'Failed to load trace');
    } finally {
      setTraceLoading(false);
    }
  }, [setSelectedSessionId]);

  const handleCloseTrace = useCallback(() => {
    setTraceSessionId(null);
    setTraceDetails(null);
    setTraceLoading(false);
    setTraceError(null);
  }, []);

  // ---- Preview handlers ----
  const handleProposalGroupClick = useCallback(
    (groupIndex: number) => {
      if (!activeSession) {
        return;
      }
      const group = groupedProposals[groupIndex];
      if (!group) {
        return;
      }
      const groupIndices = Array.from(
        { length: group.endIndex - group.startIndex + 1 },
        (_, index) => group.startIndex + index
      );
      const nextIndices = new Set(selectedPreviewIndices);
      for (const index of groupIndices) {
        nextIndices.add(index);
      }
      const sortedIndices = Array.from(nextIndices).sort((left, right) => left - right);
      setSelectedPreviewIndices(sortedIndices);
      setPreviewIndex(sortedIndices.length > 0 ? sortedIndices[sortedIndices.length - 1] : null);
      onPreviewSession?.(sessionPreviewProposals, sortedIndices);
    },
    [
      activeSession,
      groupedProposals,
      onPreviewSession,
      selectedPreviewIndices,
      sessionPreviewProposals,
    ]
  );

  const handleProposalGroupUnrender = useCallback(
    (groupIndex: number) => {
      if (!activeSession) {
        return;
      }
      const group = groupedProposals[groupIndex];
      if (!group) {
        return;
      }
      const nextIndices = selectedPreviewIndices.filter(
        (index) => index < group.startIndex || index > group.endIndex
      );
      setSelectedPreviewIndices(nextIndices);
      setPreviewIndex(nextIndices.length > 0 ? nextIndices[nextIndices.length - 1] : null);
      onPreviewSession?.(sessionPreviewProposals, nextIndices);
    },
    [
      activeSession,
      groupedProposals,
      onPreviewSession,
      selectedPreviewIndices,
      sessionPreviewProposals,
    ]
  );

  const renderSessionGroup = useCallback(
    (session: ProposalSession, group: ProposalGroup) => {
      const { groups, proposals } = buildSessionPreviewModel(session);
      if (groups.length === 0) {
        return;
      }
      const targetGroup = groups.find((candidate) => candidate.groupId === group.groupId) ?? group;
      const groupIndices = Array.from(
        { length: targetGroup.endIndex - targetGroup.startIndex + 1 },
        (_, index) => targetGroup.startIndex + index
      );

      setSelectedSessionId(session.sessionId);
      setSelectedPreviewIndices(groupIndices);
      setPreviewIndex(groupIndices.length > 0 ? groupIndices[groupIndices.length - 1] : null);
      onPreviewSession?.(proposals, groupIndices);
    },
    [onPreviewSession, setSelectedSessionId]
  );

  const toggleProposalGroup = useCallback((groupId: string) => {
    setExpandedGroupIds((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }));
  }, []);

  const handleLocalClick = (endIndex: number) => {
    const next = previewIndex === endIndex ? null : endIndex;
    setPreviewIndex(next);
    onPreviewLocalIndex?.(localPreviewQueue, next);
  };

  // ---- Tree action handlers ----
  const handleApprove = async (sessionId: string) => {
    // Block approval if any step in the current proposal chain has been rejected.
    const hasRejectedStep = sessionHasRejectedSteps(activeSession);
    if (hasRejectedStep) {
      alert('Cannot approve a session that contains rejected steps. Reject the session instead.');
      return;
    }

    setBusy(true);
    try {
      await proposalApi.approveSession(sessionId);
      removeSessionFromSnapshot(sessionId);
      onProposalActioned?.();
    } catch (err) {
      await fetchSessions();
      alert('Approve failed: ' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const promptImmediateReanalysis = async (session: ProposalSession): Promise<void> => {
    if (!session.sourceIndex || !session.sourceDocumentId) {
      return;
    }

    const confirmed = window.confirm('Would you like to immediately reanalyze the document?');
    if (!confirmed) {
      return;
    }

    const requestedSessionID =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const sessionTitle = (session.title || `Reanalyze ${session.sourceDocumentId}`).trim();
    const analyzeRequest: AnalyzeSessionRequest = {
      source_type: 'document',
      index_name: session.sourceIndex,
      document_id: session.sourceDocumentId,
      chunk_id: session.sourceChunkId,
      source: session.source,
    };

    const created = await proposalApi.createSession({
      session_id: requestedSessionID,
      title: sessionTitle,
      depends_upon_session: 'live',
    });

    const response = await curationApi.analyzeSession(created.sessionId, analyzeRequest);
    alert(`Reanalysis submitted. Foundry invoked: ${response.foundry_invoked ? 'yes' : 'no'}.`);
    onProposalActioned?.();
  };

  const handleRejectSession = async (sessionId: string) => {
    const session = sessions.find((candidate) => candidate.sessionId === sessionId);
    const hasRejectedSteps = sessionHasRejectedSteps(session ?? null);
    const hasGroupedSteps = sessionHasGroupedSteps(session ?? null);
    const shouldRejectRollup = hasRejectedSteps;
    const actionLabel = shouldRejectRollup ? 'Reject' : 'Reset';
    let actionCompleted = false;

    setBusy(true);
    try {
      let warning: string | undefined;
      if (shouldRejectRollup) {
        const resp = await proposalApi.rejectSession(sessionId, {});
        warning = resp.warning;
      } else {
        await proposalApi.cancelSession(sessionId);
      }
      removeSessionFromSnapshot(sessionId);
      actionCompleted = true;
      if (warning) {
        alert(`${actionLabel} completed with warning: ${warning}`);
      }
      if (session && (shouldRejectRollup || hasGroupedSteps)) {
        try {
          await promptImmediateReanalysis(session);
        } catch (err) {
          alert(`Reanalysis failed after ${actionLabel.toLowerCase()}: ` + (err as Error).message);
        }
      }
    } catch (err) {
      if (!actionCompleted) {
        await fetchSessions();
        alert(`${actionLabel} failed: ` + (err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCancelSession = async (sessionId: string) => {
    setBusy(true);
    try {
      if (onCancelSession) {
        await onCancelSession(sessionId);
      } else {
        await proposalApi.cancelSession(sessionId);
      }
      removeSessionFromSnapshot(sessionId);
    } catch (err) {
      await fetchSessions();
      alert('Cancel failed: ' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleModify = (parentSessionId: string) => {
    onModify?.(parentSessionId);
  };

  const handleResume = (sessionId: string) => {
    onResume?.(sessionId);
  };

  const handleRejectStep = useCallback(
    async (stepId: string, reason: string) => {
      if (!activeSession) return;
      setBusy(true);
      try {
        await proposalApi.rejectSessionStep(activeSession.sessionId, {
          proposal_step_id: stepId,
          rejection_reason: reason,
        });
        onProposalActioned?.();
        // Reload the chain to reflect the rejection
        const updated = await proposalApi.getSession(activeSession.sessionId);
        setActiveChainProposals(sessionStepsToPreviewEntries(flattenSessionSteps(updated)));
        setSessionTree((prev) => replaceSessionInTree(prev, updated));
      } catch (err) {
        alert('Step reject failed: ' + (err as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [activeSession, onProposalActioned]
  );

  const handleUnrejectStep = useCallback(
    async (stepId: string) => {
      if (!activeSession) return;
      setBusy(true);
      try {
        await proposalApi.unrejectSessionStep(activeSession.sessionId, {
          proposal_step_id: stepId,
        });
        onProposalActioned?.();
        const updated = await proposalApi.getSession(activeSession.sessionId);
        setActiveChainProposals(sessionStepsToPreviewEntries(flattenSessionSteps(updated)));
        setSessionTree((prev) => replaceSessionInTree(prev, updated));
      } catch (err) {
        alert('Step restore failed: ' + (err as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [activeSession, onProposalActioned]
  );

  const sessionStepContent = useMemo<Record<string, React.ReactNode>>(() => {
    const content: Record<string, React.ReactNode> = {};

    for (const session of sessions) {
      const groups = sessionProposalGroups(session);
      if (!groups.length) {
        continue;
      }

      const flattenedSteps = flattenSessionSteps(session);
      const isSelectedSession = activeSession?.sessionId === session.sessionId;

      content[session.sessionId] = (
        <div className="rp-timeline">
          {groups.map((group, gi) => {
            const isExpanded = expandedGroupIds[group.groupId] ?? false;
            const isRendered =
              isSelectedSession &&
              group.members.every((_, memberIndex) =>
                selectedPreviewIndices.includes(group.startIndex + memberIndex)
              );
            const isGroupActive =
              isSelectedSession &&
              group.members.some((_, memberIndex) =>
                selectedPreviewIndices.includes(group.startIndex + memberIndex)
              );
            return (
              <div key={group.groupId} className="rp-group">
                <div
                  className={`rp-group-header${isGroupActive ? ' rp-group-header--active' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    toggleProposalGroup(group.groupId);
                    if (isSelectedSession) {
                      handleProposalGroupClick(gi);
                    } else {
                      renderSessionGroup(session, group);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') {
                      return;
                    }
                    e.preventDefault();
                    toggleProposalGroup(group.groupId);
                    if (isSelectedSession) {
                      handleProposalGroupClick(gi);
                    } else {
                      renderSessionGroup(session, group);
                    }
                  }}
                >
                  <span className="rp-group-header__left">
                    <i className="modus-icons" aria-hidden="true">
                      {isExpanded ? 'expand_less' : 'expand_more'}
                    </i>
                    <span className="rp-group-header__title">
                      {proposalGroupDisplayTitle(group)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="rp-group-header__meta rp-group-header__visibility-btn"
                    aria-label={
                      isRendered
                        ? 'Unrender this group and successor groups'
                        : 'Render this group in preview'
                    }
                    title={
                      isRendered
                        ? 'Unrender this group and successor groups'
                        : 'Render this group in preview'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isRendered) {
                        handleProposalGroupUnrender(gi);
                      } else if (isSelectedSession) {
                        handleProposalGroupClick(gi);
                      } else {
                        renderSessionGroup(session, group);
                      }
                    }}
                  >
                    <i className="modus-icons" aria-hidden="true">
                      {isRendered ? 'visibility_on' : 'visibility_off'}
                    </i>
                  </button>
                </div>
                {isExpanded && (
                  <div className="rp-group-steps">
                    {group.members.map((member, memberIndex) => {
                      const stepId = member.proposal_step_id?.trim() || '';
                      const step = flattenedSteps.find((candidate) => candidate.step_id === stepId);
                      const stepRejectionReason = step?.rejection_reason ?? '';
                      const stepIsRejected = Boolean(stepRejectionReason);
                      return (
                        <ProposalItem
                          key={`${group.groupId}-${stepId || memberIndex}`}
                          proposal={member}
                          step={step}
                          index={group.startIndex + memberIndex}
                          highlighted={
                            isSelectedSession &&
                            selectedPreviewIndices.includes(group.startIndex + memberIndex)
                          }
                          disablePreviewClick
                          relCount={undefined}
                          stepId={stepId}
                          canReject={isSelectedSession && canApprove && Boolean(handleRejectStep)}
                          onRejectStep={isSelectedSession ? handleRejectStep : undefined}
                          onUnrejectStep={isSelectedSession ? handleUnrejectStep : undefined}
                          isRejected={stepIsRejected}
                          rejectionReason={stepRejectionReason}
                          busy={busy}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return content;
  }, [
    sessions,
    activeSession?.sessionId,
    expandedGroupIds,
    selectedPreviewIndices,
    toggleProposalGroup,
    handleProposalGroupClick,
    handleProposalGroupUnrender,
    canApprove,
    busy,
    renderSessionGroup,
    handleRejectStep,
    handleUnrejectStep,
  ]);

  return (
    <div className="review-panel">
      {error && <div className="rp-error">{error}</div>}

      {/* ---- Session tree ---- */}
      <div className="rp-session-selector">
        <SessionTree
          tree={sessionTree}
          activeSessionId={activeSessionId}
          expandedSummarySessionIds={expandedSummarySessionIds}
          expandedStepsSessionIds={expandedStepsSessionIds}
          sessionSummaries={sessionSummaries}
          sessionStepContent={sessionStepContent}
          onToggleSummary={toggleSessionSummary}
          onToggleSteps={toggleSessionSteps}
          onShowSourceText={onViewSource ? handleShowSessionSource : undefined}
          onShowTrace={handleShowTrace}
          onResume={handleResume}
          onCancel={handleCancelSession}
          onModify={handleModify}
          onApprove={canApprove ? handleApprove : undefined}
          onReject={canApprove ? handleRejectSession : undefined}
          onSubmitSession={onSaveSession}
          onCancelActiveSession={onCancelActiveSession}
          approveBlockedSessionId={
            sessionHasRejectedSteps(activeSession)
              ? selectedSessionId !== LOCAL_SESSION_KEY
                ? (selectedSessionId ?? undefined)
                : undefined
              : undefined
          }
          rejectModeSessionId={
            sessionHasRejectedSteps(activeSession)
              ? selectedSessionId !== LOCAL_SESSION_KEY
                ? (selectedSessionId ?? undefined)
                : undefined
              : undefined
          }
          busy={busy}
        />
        {traceSessionId && (
          <FoundryTraceOverlay
            details={traceDetails}
            loading={traceLoading}
            error={traceError}
            onClose={handleCloseTrace}
          />
        )}
        {localQueue.length > 0 && (
          <button
            className={`st-node-btn${isLocal ? ' st-node-btn--selected' : ''}`}
            style={{ paddingLeft: 8, marginTop: 2 }}
            onClick={() => setSelectedSessionId(LOCAL_SESSION_KEY)}
          >
            <span className="st-node-label">Local session ({localQueue.length} staged)</span>
          </button>
        )}
      </div>

      {/* ---- Session status banner for closed sessions ---- */}
      {!isLocal &&
        activeSession &&
        (activeSession.status === 'approved' ||
          activeSession.status === 'rejected' ||
          activeSession.status === 'error' ||
          activeSession.validationStatus === 'error') && (
          <div className={`rp-session-status rp-session-status--${activeSession.status}`}>
            <span className="rp-session-status__label">
              {activeSession.status === 'approved'
                ? 'Approved'
                : activeSession.status === 'rejected'
                  ? 'Rejected'
                  : activeSession.validationStatus === 'error'
                    ? 'Validation Error'
                    : 'Error'}
            </span>
            {activeSession.errorDetail && (
              <span className="rp-session-status__detail">
                {activeSession.errorStage ?? 'error'}: {activeSession.errorDetail}
              </span>
            )}
          </div>
        )}

      {/* ---- Proposal timeline ---- */}
      <div className="rp-timeline">
        {isLocal ? (
          localQueue.length === 0 ? (
            <div className="rp-empty">No staged edits.</div>
          ) : (
            groupedLocalEntries.map((group, gi) => {
              const relCount = group.members.filter(
                (member) => member.entity_type === 'relationship'
              ).length;
              return (
                <StagedEntryRow
                  key={`${group.lead.entity_type}-${group.lead.node_id ?? group.lead.from_node_id ?? gi}-${gi}`}
                  entry={group.lead}
                  index={gi}
                  highlighted={
                    previewIndex !== null &&
                    previewIndex >= group.startIndex &&
                    previewIndex <= group.endIndex
                  }
                  onClick={() => handleLocalClick(group.endIndex)}
                  relCount={relCount}
                />
              );
            })
          )
        ) : !activeSession || activeChainProposals.length === 0 ? (
          <div className="rp-empty">No proposals in this session.</div>
        ) : null}
      </div>

      {previewIndex !== null && (
        <div className="rp-preview-hint">
          Preview at step{' '}
          {Math.max(
            0,
            (isLocal ? groupedLocalEntries : groupedProposals).findIndex(
              (g) => previewIndex >= g.startIndex && previewIndex <= g.endIndex
            )
          ) + 1}{' '}
          — click again to clear
        </div>
      )}
    </div>
  );
};

export default ReviewPanel;
